import React from 'react';

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug } from '@fortawesome/free-solid-svg-icons';

import { pickPrinterDevice } from '../../utils/printerSettings';
import {
  getAllPrinterSettings,
  createPrinterSettings,
  updatePrinterSettings,
} from '../../api/printerSettings';

import styles from './PrintOptionsModal.module.scss';

// tasks: [{ key, label }] — key is the PRINTER_TASKS.<name>.key string
const PrinterSettingsModal = ({ show, onHide, tasks = [], token }) => {
  const [printers, setPrinters] = React.useState({});
  const [settingPrinterFor, setSettingPrinterFor] = React.useState(null);

  React.useEffect(() => {
    if (!show) return;
    const loadSavedPrinters = async () => {
      try {
        const allSettings = await getAllPrinterSettings(token);
        const loaded = {};
        (allSettings || []).forEach((setting) => {
          loaded[setting.taskKey] = setting;
        });
        setPrinters(loaded);
      } catch (error) {
        console.error('Printer settings: failed to load saved printers', error);
      }
    };
    loadSavedPrinters();
  }, [show, token]);

  const handleSetPrinter = async (taskKey, taskLabel) => {
    setSettingPrinterFor(taskKey);
    try {
      const device = await pickPrinterDevice();
      // Already have a printer saved for this task — update it instead of
      // creating a second entry for the same task.
      const saved = printers[taskKey]
        ? await updatePrinterSettings(taskKey, device, token)
        : await createPrinterSettings({ taskKey, ...device }, token);
      setPrinters((prev) => ({ ...prev, [taskKey]: saved }));
      toast.success(`Printer set for ${taskLabel}`);
    } catch (error) {
      console.error(`Printer settings: failed to set printer for ${taskKey}`, error);
      toast.error('Could not set printer. Please try again.');
    }
    setSettingPrinterFor(null);
  };

  return (
    <>
      {show && <div className={styles.customBackdrop} onClick={onHide} />}
      <Modal show={show} onHide={onHide} backdrop={false} centered className={styles.modal}>
        <Modal.Header closeButton>
          <Modal.Title>Printer Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tasks.map(({ key, label }) => {
            const saved = printers[key];
            return (
              <div className={styles.section} key={key}>
                <div className={styles.printerRow} style={{ borderTop: 'none' }}>
                  <span className={styles.printerStatus}>
                    <strong>{label}</strong>
                    <br />
                    {saved ? (
                      <>Printer: {saved.productName}</>
                    ) : (
                      'No printer set — will ask each time'
                    )}
                  </span>
                  <button
                    type='button'
                    className={styles.setPrinterBtn}
                    disabled={settingPrinterFor === key}
                    onClick={() => handleSetPrinter(key, label)}
                  >
                    <FontAwesomeIcon icon={faPlug} />
                    {settingPrinterFor === key
                      ? 'Setting…'
                      : saved
                      ? 'Change printer'
                      : 'Set printer'}
                  </button>
                </div>
              </div>
            );
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={onHide}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PrinterSettingsModal;
